import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    if (!token) return toast.error("This reset link is missing its token. Please request a new one.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="mx-auto max-w-md px-6 py-16">
      <Card className="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        {done ? (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald/10 text-emerald">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <h1 className="text-center font-serif text-3xl text-forest">Password updated</h1>
            <p className="mt-2 text-center text-sm text-muted-foreground" data-testid="reset-success-message">
              You can sign in with your new password now. Redirecting you to sign-in…
            </p>
            <Link to="/login" className="mt-6 block text-center text-sm font-medium text-primary hover:underline" data-testid="reset-go-to-login">
              Go to sign in <ArrowLeft className="ml-1 inline h-3.5 w-3.5 rotate-180" />
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-serif text-3xl text-forest">Choose a new password</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Must be at least 8 characters. Pick something you'll remember.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground">New password</label>
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  data-testid="reset-password-input"
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Confirm new password</label>
                <Input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Type it again"
                  data-testid="reset-confirm-input"
                  className="mt-1 h-11"
                />
              </div>
              <Button type="submit" disabled={submitting || !token} data-testid="reset-submit-btn" className="w-full rounded-full bg-primary py-6 text-primary-foreground">
                {submitting ? "Updating…" : "Update password"}
              </Button>
              {!token && (
                <p className="text-center text-sm text-destructive" data-testid="reset-missing-token">
                  This reset link is missing or invalid. <Link to="/forgot-password" className="font-medium underline">Request a new one</Link>.
                </p>
              )}
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
